import QButton from 'components/QButton';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { transactionsActions, transactionsSelectors } from 'app-components/flux/transactions';
import { tracker } from 'app-components/utils/core';

import { CircularProgress, Tooltip, Typography } from '@mui/material';
import { actionHelpers } from 'app-components/flux/core';
import { DateTime } from 'luxon';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  adviceSurface: {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: theme.palette.info.light,
    borderRadius: 8,
    padding: '8px 16px',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  infoText: {
    color: theme.palette.text.secondary,
  },
  bold: {
    color: theme.palette.text.secondary,
    fontWeight: 'bold',
  },
  loadingIcon: {
    marginLeft: 4,
    marginRight: 4,
    color: theme.palette.primary.main,
  },
}));

type LoadAllTransactionsProps = {
  tooltipText: string;
  initiator: string;
};

const LoadAllTransactionsHelper = ({ tooltipText, initiator }: LoadAllTransactionsProps) => {
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const [loading, setLoading] = useState(false);

  const dateOnAfterISO = useSelector(transactionsSelectors.getDateOnAfter) ?? '';
  const minDateISO = useSelector(transactionsSelectors.getEarliestDateOn);
  const yearOnAfter = DateTime.fromISO(dateOnAfterISO ?? '').plus({ days: 1 }).year;
  const isAllTime = !minDateISO || dateOnAfterISO < minDateISO;

  const loadAllTransactions = () => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const dateOnAfterAllTxns = DateTime.fromISO(minDateISO!).minus({ days: 1 }).toISODate();
    tracker.track(tracker.events.transactionsOnDemand, {
      selectionType: 'temporary',
      date: dateOnAfterAllTxns,
      earliestDate: minDateISO,
      initiator,
    });
    setLoading(true);
    actionHelpers
      .bindPromiseAction(dispatch, transactionsActions.getTransactions)(undefined, {
        props: { dateOnAfter: dateOnAfterAllTxns },
      })
      .finally(() => setLoading(false))
      .catch((e) => assert(false, e));
  };

  return !isAllTime ? (
    <div className={classes.adviceSurface}>
      <Typography variant='captionRegular' flexWrap='wrap' className={classes.infoText}>
        {'Please note this change will apply to transactions'}
        <span className={classes.bold}>{` Since ${yearOnAfter} `}</span>
        {'only.'}
      </Typography>
      <Tooltip title={tooltipText} arrow placement={'bottom-end'} disableInteractive>
        <QButton
          id='load-all-txns'
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} className={classes.loadingIcon} /> : undefined}
          onClick={loadAllTransactions}
          style={{ textTransform: 'none' }}
        >
          {'Load All'}
        </QButton>
      </Tooltip>
    </div>
  ) : null;
};

export default LoadAllTransactionsHelper;
